<template>
  <div class="roller">

    <!-- Push the Limit modal -->
    <b-modal
        id="pushLimitModal"
        title="Push the Limit!"
        @ok="pushLimitOk">
      <b-form-group label="Enter your Edge attribute:">
        <b-form-input v-model="userData.edge" type="number" :formatter="zeroOrGreater" lazy-formatter />
      </b-form-group>
    </b-modal>

    <!-- Clear history modal -->
    <b-modal
        id="clearRollHistoryModal"
        ok-title="Yes"
        :hide-header="true"
        @ok="clearRollHistory">
      <h5>
        <span class="text-warning mr-2"><font-awesome-icon icon="exclamation-triangle" /></span>
        Are you sure you want to delete your entire roll history?
      </h5>
    </b-modal>

    <b-row>
      <b-col lg="4">
        <!-- Roll settings -->
        <b-card
            no-body
            border-variant="primary"
            class="mb-2 roller-settings">

          <b-tabs card>

            <b-tab title="Roll" active>
              <b-form-group class="mb-0">

                <b-row>
                  <b-col cols="12">
                    <b-form-group label="Label"
                        description="Optional! Use this to remember why you rolled.">

                      <div class="d-flex">

                        <b-input-group style="margin-right: -1px"><!-- Remove the mysterious 1px space -->
                          <b-input-group-button>
                            <b-dropdown>
                              <b-dropdown-header>Hit presets{{hitsPresets.length === 0 ? ' (none saved yet!)' : ''}}</b-dropdown-header>

                              <b-dropdown-item
                                  v-for="preset in hitsPresets"
                                  :key="preset.label"
                                  @click="loadPreset(preset)">
                                {{preset.label}}
                                <span class="text-muted">
                                  &mdash;
                                  <font-awesome-icon icon="clone" fixed-width /> {{preset.dicePool}}
                                  <font-awesome-icon icon="arrow-to-top" fixed-width /> {{preset.useLimit ? preset.limit : 'N/A'}}
                                </span>
                              </b-dropdown-item>

                              <b-dropdown-divider />
                              <b-dropdown-header>Sum presets{{sumPresets.length === 0 ? ' (none saved yet!)' : ''}}</b-dropdown-header>

                              <b-dropdown-item
                                  v-for="preset in sumPresets"
                                  :key="preset.label"
                                  @click="loadPreset(preset)">
                                {{preset.label}}
                                <span class="text-muted">
                                  &mdash;
                                  {{preset.baseValue}} + {{preset.dicePool}}d6
                                </span>
                              </b-dropdown-item>

                            </b-dropdown>
                          </b-input-group-button>
                          <b-form-input v-model="userData.rollSettings.label" />
                        </b-input-group>

                        <b-button
                            class="ml-1"
                            v-b-tooltip.window.hover title="Save preset"
                            variant="success"
                            @click="savePreset">
                          <font-awesome-icon icon="save" fixed-width />
                        </b-button>

                        <b-button
                            class="ml-1"
                            v-b-tooltip.window.hover title="Delete preset"
                            variant="danger"
                            @click="deletePreset">
                          <font-awesome-icon icon="trash" fixed-width />
                        </b-button>
                      </div>

                    </b-form-group>
                  </b-col>


                  <b-col cols="12">
                    <b-form-group label="Mode">
                      <div class="text-center">
                        <b-form-radio-group
                            buttons
                            button-variant="outline-primary text-white"
                            v-model="userData.rollSettings.mode"
                            name="radiosBtnDefault">

                          <b-form-radio
                              value="hits"
                              v-b-tooltip.window.hover title="Count hits and misses. Use this for most tests.">
                            <font-awesome-icon icon="crosshairs" fixed-width /> Hits
                          </b-form-radio>

                          <b-form-radio
                              value="sum"
                              v-b-tooltip.window.hover title="Count the total roll. Use this for e.g. initiative.">
                            <font-awesome-icon icon="plus" fixed-width /> Sum
                          </b-form-radio>

                        </b-form-radio-group>
                      </div>
                    </b-form-group>
                  </b-col>

                  <b-col md="6">
                    <b-form-group>
                      <span slot="label">
                        <font-awesome-icon icon="clone" fixed-width /> Dice pool
                      </span>
                      <b-form-input v-model="userData.rollSettings.dicePool" type="number" :formatter="zeroOrGreater" lazy-formatter />
                    </b-form-group>
                  </b-col>

                  <b-col md="6">
                    <b-form-group v-if="userData.rollSettings.mode === 'hits'">
                      <span slot="label">
                        <font-awesome-icon icon="arrow-to-top" fixed-width /> Limit
                        </span>

                      <b-input-group>
                        <b-input-group-addon>
                          <input
                            type="checkbox"
                            v-model="userData.rollSettings.useLimit"
                            v-b-tooltip.window.hover title="Uncheck to ignore limit.">
                        </b-input-group-addon>
                        <b-form-input
                            v-model="userData.rollSettings.limit"
                            type="number"
                            :formatter="zeroOrGreater"
                            :disabled="!userData.rollSettings.useLimit"
                            lazy-formatter />
                      </b-input-group>
                    </b-form-group>

                    <b-form-group v-if="userData.rollSettings.mode === 'sum'">
                      <span slot="label">
                        <font-awesome-icon icon="arrow-to-bottom" fixed-width /> Base value
                      </span>
                      <b-form-input v-model="userData.rollSettings.baseValue" type="number" :formatter="zeroOrGreater" lazy-formatter />
                    </b-form-group>
                  </b-col>
                </b-row>

                <div class="text-center">
                  <b-btn
                      class="mr-1"
                      variant="primary"
                      size="lg"
                      id="rollDiceButton"
                      @click="createRoll(false)">
                    <font-awesome-icon icon="sync" fixed-width />
                  </b-btn>

                  <b-btn
                      variant="outline-warning"
                      size="lg"
                      id="pushLimitButton"
                      @click="pushLimit(null)"
                      v-if="userData.rollSettings.mode === 'hits'">
                    <font-awesome-icon icon="chevron-double-up" fixed-width />
                  </b-btn>

                  <b-tooltip
                      target="rollDiceButton"
                      ref="rollDiceTooltip"
                      triggers="hover">
                    Roll the dice!
                  </b-tooltip>

                  <b-tooltip
                      target="pushLimitButton"
                      ref="pushLimitTooltip"
                      triggers="hover"
                      v-if="userData.rollSettings.mode === 'hits'">
                    <h6>Push the Limit</h6>
                    Spend a point of Edge to add your Edge rating to the dice pool, reroll sixes for extra hits, and ignore the limit.
                  </b-tooltip>
                </div>
              </b-form-group>
            </b-tab>

            <b-tab title="Settings">
              <b-form-checkbox v-model="userData.animateDice">
                Animate dice
              </b-form-checkbox>

              <div class="text-center">
                <b-btn variant="outline-danger"
                    size="lg"
                    v-b-modal.clearRollHistoryModal>
                  Clear roll history
                </b-btn>
              </div>
            </b-tab>
          </b-tabs>

        </b-card>
      </b-col>

      <b-col lg="8">
        <!-- Roll history -->
        <roll-info
            v-for="roll in reversedHistory"
            :key="roll.id"
            :roll="roll"
            :animate="rollsToAnimate.indexOf(roll.id) !== -1 && userData.animateDice"
            @deletedRoll="deleteRoll"
            @usedPushTheLimit="pushLimit"
            @usedSecondChance="secondChance"
            @usedCloseCall="closeCall" />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Random from 'random-js'

let random = new Random(Random.engines.mt19937().autoSeed())

let defaultUserData = {
  rollSettings: {
    dicePool: 0,
    limit: 0,
    baseValue: 0,
    useLimit: true,
    label: '',
    mode: 'hits'
  },

  edge: 0,
  presets: [],
  rollHistory: [],

  animateDice: true
}

import deepCopy from '../../helper/deepCopy.js'
import diceTools from '../../mixins/diceTools.js'
import formatters from '../../mixins/formatters.js'
import storage from '../../mixins/storage.js'

import rollInfo from './roll-info.vue'
import FontAwesomeIcon from '@fortawesome/vue-fontawesome'

import EdgeMode from './edgeMode.js'

export default {
  name: 'roller',
  mixins: [ storage, formatters, diceTools ],
  components: { rollInfo, FontAwesomeIcon },

  data () {
    return {
      dataId: 'roller',
      defaultUserData: defaultUserData,
      pushLimitRollId: null,

      // Only rolls in this list will animate (so page reload doesn't re-animate)
      rollsToAnimate: []
    }
  },

  methods: {
    // Add a roll to the list of rolls to animate if it's not already on the list
    addRollToAnimate (rollId) {
      if (this.rollsToAnimate.indexOf(rollId) !== -1) return

      this.rollsToAnimate.push(rollId)
    },

    // Delete all roll history events
    clearRollHistory () {
      this.userData.rollHistory = []
    },

    // Use Close Call on the given roll.
    closeCall (rollId) {
      this.getRoll(rollId).edgeMode = EdgeMode.CLOSE_CALL
    },

    // Roll the given dice pool and add it to the roll history
    createRoll (toPushTheLimit) {
      let dicePool = parseInt(this.userData.rollSettings.dicePool)
      if (toPushTheLimit) dicePool += parseInt(this.userData.edge)

      let dice = []
      this.rollDice(dice, dicePool, toPushTheLimit)

      let date = new Date()
      let newId = this.nextRollSettingsId()

      let newRoll = {
        dicePool: this.userData.rollSettings.dicePool,
        label: this.userData.rollSettings.label || date.toLocaleTimeString(),
        dice: dice,
        id: newId,
        date: date.toISOString(),
        edge: this.userData.edge,
        edgeMode: toPushTheLimit ? EdgeMode.PUSH_LIMIT_PRE : EdgeMode.NONE,
        mode: this.userData.rollSettings.mode
      }

      if (newRoll.mode === 'hits') {
        newRoll.limit = this.userData.rollSettings.useLimit ? this.userData.rollSettings.limit : null
      }
      else if (newRoll.mode === 'sum') {
        newRoll.baseValue = this.userData.rollSettings.baseValue
      }

      this.userData.rollHistory.push(newRoll)

      this.addRollToAnimate(newId)
    },

    // Delete the current roll setting from the list of presets.
    deletePreset () {
      let presetIndex = this.getPresetIndex(this.userData.rollSettings.label)

      if (presetIndex === -1) return

      this.userData.presets.splice(presetIndex, 1)
    },

    // Delete a specific roll
    deleteRoll (rollId) {
      let rollIndex = this.userData.rollHistory.findIndex(roll => roll.id === rollId)
      this.userData.rollHistory.splice(rollIndex, 1)

      let toAnimateIndex = this.rollsToAnimate.indexOf(rollId)
      if (toAnimateIndex !== -1) {
        this.rollsToAnimate.splice(toAnimateIndex, 1)
      }
    },

    // Get a preset index by its label.
    getPresetIndex (label) {
      return this.userData.presets.findIndex(preset => preset.label === label)
    },

    // Get a roll by its id.
    getRoll (rollId) {
      return this.userData.rollHistory.find(roll => roll.id === rollId)
    },

    // Replace the roll settings with the given preset.
    loadPreset (preset) {
      this.userData.rollSettings = deepCopy(preset)
    },

    // Sorted list of presets that use a given mode
    modePresets (mode) {
      let presets = []

      for (let i = 0; i < this.userData.presets.length; ++i) {
        let preset = this.userData.presets[i]
        if (preset.mode === mode) presets.push(preset)
      }

      presets.sort((a, b) => a.label.localeCompare(b.label))

      return presets
    },

    // Get the next roll id to use
    nextRollSettingsId () {
      // Get the maximum id (ignoring NaN) and add 1 to get the next id
      return this.userData.rollHistory.reduce((max, roll) => isNaN(roll.id) ? max : Math.max(max, roll.id), 0) + 1
    },

    // Called when OK is pressed in the Push the Limit modal.
    pushLimitOk () {
      if (this.pushLimitRollId === null) {
        this.createRoll(true)
      }
      else {
        let roll = this.getRoll(this.pushLimitRollId)

        this.rollDice(roll.dice, this.userData.edge, true)
        roll.edgeMode = EdgeMode.PUSH_LIMIT_POST

        this.addRollToAnimate(roll.id)
      }
    },

    // Open the modal to Push the Limit for the given roll. If null, then a new roll is created.
    pushLimit (rollId) {
      this.pushLimitRollId = rollId
      this.$root.$emit('bv::show::modal', 'pushLimitModal')
    },

    // Roll the given number of dice and add the result to the given list.
    // If explode is true, sixes explode to roll more dice.
    rollDice (rollList, dicePool, explode) {
      let diceToRoll = dicePool

      while (diceToRoll > 0) {
        let result = []
        for (let i = 0; i < diceToRoll; ++i) {
          result.push(random.integer(1, 6))
        }

        rollList.push(result)

        // Sixes don't explode, so we don't want to loop
        if (!explode) break

        // Re-roll any sixes we just rolled
        diceToRoll = result.reduce((sixes, value) => value === 6 ? sixes + 1 : sixes, 0)
      }
    },

    // Save the current roll settings as a preset.
    savePreset () {
      let rollSettings = this.userData.rollSettings
      if (!rollSettings.label) return

      // Deep copy the preset so we don't change it once it's been saved
      let settingsCopy = deepCopy(rollSettings)

      // Check if the preset already exists
      let presetIndex = this.getPresetIndex(rollSettings.label)

      if (presetIndex === -1) {
        this.userData.presets.push(settingsCopy)
      }
      else {
        this.userData.presets.splice(presetIndex, 1, settingsCopy)
      }
    },

    // Use Second Chance on the given roll.
    secondChance (rollId) {
      let roll = this.getRoll(rollId)
      roll.edgeMode = EdgeMode.SECOND_CHANCE

      // Re-roll dice equal to the number of non-hit dice
      let hits = this.calcTotalHits(roll.dice)
      this.rollDice(roll.dice, roll.dicePool - hits, false)

      this.addRollToAnimate(roll.id)
    }
  },

  computed: {
    // Dice roll history in reverse order
    reversedHistory () {
      return this.userData.rollHistory.slice().reverse()
    },

    // Sorted list of presets that use hits mode
    hitsPresets () {
      return this.modePresets('hits')
    },

    // Sorted list of presets that use sum mode
    sumPresets () {
      return this.modePresets('sum')
    }
  }
}
</script>



// WEBPACK FOOTER //
// Roller.vue?aa0a0098